import {Color} from '@benchling/nucleus-ui';
import * as d3 from 'd3';
import React, {useCallback, useMemo, useState} from 'react';

import {NonStandardAminoAcids} from 'biojs/Codons';
import {PathBuilder} from 'molbio/maps/linear/PathBuilder';
import {FontAvailability, getLetterSpacingCorrection} from 'molbio/maps/MonospaceFontCorrection';
import Type from 'molbio/maps/Type';
import useGlyphsByColor from 'molbio/proteins/hooks/useGlyphsByColor';
import {
  AMINO_ACID_COLORINGS,
  AminoAcidColoring,
  ProteinColorScheme,
  TRANSPARENT_SENTINEL,
} from 'molbio/proteins/util/ProteinColoringConstants';
import {BasicAminoAcidSLA, ProteinAlignmentSLA} from 'molbio/proteins/util/ProteinConstants';
import useOnMount from 'ui-library/hooks/useOnMount';

export interface AminoAcidLayout {
  visible: boolean;
  type: {
    fontSize: number;
    letterSpacing: number;
  };
  foregroundColoring: Exclude<ProteinColorScheme, ProteinColorScheme.NONE>;
  showColoring: boolean;
  vMargin: number;
  spacer: number;
}

type ColorMapping = {[aminoAcid in BasicAminoAcidSLA]: AminoAcidColoring} & {
  [aminoAcid in NonStandardAminoAcids | '-']?: AminoAcidColoring;
};

/**
 * Renders a line of colored AA residue glyphs.
 * @param color The color the glyphs should be
 * @param filteredResidues The residues of the slice, with all residues NOT of this color replaced with spaces
 */
function AminoAcidColoredGlyphs({
  color,
  filteredResidues,
}: {
  color: string;
  filteredResidues: string;
}): JSX.Element {
  return (
    <text xmlSpace="preserve" fill={color} dominantBaseline="central">
      {filteredResidues}
    </text>
  );
}

/**
 * Renders colored squares behind AA residue glyphs.
 * @param color The color the squares should be
 * @param height The height of the squares
 * @param filteredResidues The residues of the slice, with all residues NOT of this color replaced with spaces
 * @param xTransform A function to convert from base pair index within this slice to on-screen pixels
 */
function AminoAcidColoredSquares({
  color,
  filteredResidues,
  height,
  xTransform,
}: {
  color: string;
  filteredResidues: string;
  height: number;
  xTransform: (x: number) => number;
}): JSX.Element {
  const pathString = useMemo(() => {
    const pathBuilder = new PathBuilder();
    for (let i = 0; i < filteredResidues.length; i++) {
      if (filteredResidues[i] !== ' ') {
        pathBuilder.addIndex(i);
      }
    }
    const yTransform = d3.scaleLinear().domain([0, 1]).range([height, 0]);
    return PathBuilder.getDataString(pathBuilder.getPoints(), xTransform, yTransform);
  }, [filteredResidues, height, xTransform]);
  return <path d={pathString} fill={color === TRANSPARENT_SENTINEL ? 'none' : color} />;
}

/**
 * Replace any residue in `residues` not found (case-insensitive) in `targetAminoAcids` with a space.
 * @param  {string} residues The residues to filter
 * @param  {string[]} targetAminoAcids - should be restricted to alphabet and - character.
 * @return {string}
 * @package
 */
export function filteredResidues(residues: string, targetAminoAcids: Array<ProteinAlignmentSLA>): string {
  const escapedAminoAcids = targetAminoAcids.map((aminoAcid) => (aminoAcid === '-' ? '\\-' : aminoAcid));
  return residues.replace(new RegExp(`[^${escapedAminoAcids.join('')}]`, 'ig'), ' ');
}

export interface AminoAcidsProps {
  aminoAcids: string;
  layout: AminoAcidLayout;
  sliceStart: number;
  xFn: d3.ScaleLinear<number, number>;
}

export default function AminoAcids({
  aminoAcids,
  layout,
  sliceStart,
  xFn,
}: AminoAcidsProps): JSX.Element | null {
  const [areFontsReady, setAreFontsReady] = useState(FontAvailability.get().get('areFontsReady'));

  useOnMount(() => {
    const fontAvailabilityCallback = (): void => {
      setAreFontsReady(FontAvailability.get().get('areFontsReady'));
    };

    FontAvailability.get().on('change:areFontsReady', fontAvailabilityCallback);

    return () => {
      FontAvailability.get().off('change:areFontsReady', fontAvailabilityCallback);
    };
  });

  const height = new Type(layout.type.fontSize, layout.type.letterSpacing).height();
  const colorMapping: ColorMapping = AMINO_ACID_COLORINGS[layout.foregroundColoring];

  // Instead of rendering each residue as an individual <tspan> with a color (O(N) DOM nodes), we overlap
  // at most 26 (one for each amino acid color) <text> nodes, with spaces for the residues that don't have
  // that color. This drastically improves performance for alignments where a single row may have 2000+
  // residues.
  const getGlyphColor = useCallback(
    (aminoAcid: ProteinAlignmentSLA) => {
      const aaColoring = colorMapping[aminoAcid];
      if (!layout.showColoring || aaColoring == null) {
        return Color.BLACK;
      }
      return aaColoring.glyphColor;
    },
    [colorMapping, layout.showColoring]
  );
  const glyphsByColor = useGlyphsByColor({
    aminoAcids,
    getColor: getGlyphColor,
  });

  // Similarly, we render overlapping `path` nodes for each background color.
  const getBackgroundColor = useCallback(
    (aminoAcid: ProteinAlignmentSLA) => {
      const aaColoring = colorMapping[aminoAcid];
      if (!layout.showColoring || aaColoring == null) {
        return TRANSPARENT_SENTINEL;
      }
      return aaColoring.backgroundColor;
    },
    [colorMapping, layout.showColoring]
  );
  const glyphsByBackgroundColor = useGlyphsByColor({
    aminoAcids,
    getColor: getBackgroundColor,
  });

  const xTransform = useCallback((x: number) => xFn(x + sliceStart), [sliceStart, xFn]);

  let letterSpacing = layout.type.letterSpacing;
  if (areFontsReady) {
    letterSpacing += getLetterSpacingCorrection(layout.type.fontSize);
  }

  if (!layout.visible) {
    return null;
  }

  const textGTransformY = layout.type.fontSize / 2;
  const showSquares = glyphsByBackgroundColor && aminoAcids.length > 0 && layout.showColoring;

  return (
    <>
      {showSquares && (
        <g transform="translate(0,0)">
          {Object.entries(glyphsByBackgroundColor).map(([color, residues]) => (
            <AminoAcidColoredSquares
              color={color}
              filteredResidues={residues}
              height={height}
              key={`squares-${color}`}
              xTransform={xTransform}
            />
          ))}
        </g>
      )}
      <g
        fontSize={layout.type.fontSize}
        letterSpacing={letterSpacing}
        transform={`translate(${layout.type.letterSpacing / 2}, ${textGTransformY})`}
      >
        {aminoAcids.length > 0 &&
          Object.entries(glyphsByColor).map(([color, residues]) => (
            <AminoAcidColoredGlyphs color={color} filteredResidues={residues} key={`glyphs-${color}`} />
          ))}
      </g>
    </>
  );
}
